import React, { useState, useEffect } from 'react';
import MetaBudgetReviewForm from './MetaBudgetReviewForm';
import MetaBudgetReviewList from './MetaBudgetReviewList';
import MetaBudgetReviewHistory from './MetaBudgetReviewHistory';
import MetaBudgetReviewQuickActionModal from './MetaBudgetReviewQuickActionModal';
import LiveClock from './LiveClock';

const MetaBudgetReviewSection = ({ currentUser }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-budget-review`);
      
      if (response.ok) {
        const data = await response.json();
        setRecords(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar registros:', error);
      alert('Error al cargar los registros de revisión de presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (recordData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const url = editingRecord 
        ? `${API_URL}/meta-budget-review/${editingRecord.id}`
        : `${API_URL}/meta-budget-review`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...recordData,
          user_name: currentUser?.name || currentUser?.email || 'Sistema'
        }),
      });

      if (response.ok) {
        alert(editingRecord 
          ? 'Registro actualizado exitosamente' 
          : 'Registro creado exitosamente');
        setShowForm(false);
        setEditingRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      alert(`Error al guardar: ${error.message}`);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-budget-review/${recordId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Registro eliminado exitosamente');
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el registro');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(`Error al eliminar: ${error.message}`);
    }
  };

  const handleShowHistory = (record) => {
    setSelectedRecord(record);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      // Si es un cambio de estado, obtener el registro completo y actualizarlo
      if (actionData.action_type === 'status_changed') {
        // Obtener el registro actual
        const currentRecord = records.find(r => r.id === recordId);
        if (!currentRecord) {
          throw new Error('Registro no encontrado');
        }

        // Crear objeto con todos los campos necesarios
        const updateData = {
          mes: currentRecord.mes,
          agente_seguimiento: currentRecord.agente_seguimiento,
          cliente: currentRecord.cliente,
          cuenta_publicitaria: currentRecord.cuenta_publicitaria,
          celula: currentRecord.celula,
          nombre_campana: currentRecord.nombre_campana,
          fecha_pago: currentRecord.fecha_pago ? new Date(currentRecord.fecha_pago).toISOString().split('T')[0] : null,
          monto_pago: currentRecord.monto_pago,
          asunto: currentRecord.asunto,
          acciones_tomadas: currentRecord.acciones_tomadas,
          estado: actionData.new_status // Solo cambiar el estado
        };

        const updateResponse = await fetch(`${API_URL}/meta-budget-review/${recordId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Error al actualizar el estado');
        }
      }

      // Luego registrar la acción en el historial
      const response = await fetch(`${API_URL}/meta-budget-review/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (response.ok) {
        alert('Acción registrada exitosamente');
        setShowQuickAction(false);
        setQuickActionRecord(null);
        
        // Recargar la lista para reflejar los cambios
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al registrar la acción');
      }
    } catch (error) {
      console.error('Error al registrar acción:', error);
      throw error; // Re-lanzar para que el modal pueda manejarlo
    }
  };

  // Función para obtener el color según el porcentaje de presupuesto restante
  const getBudgetStatusColor = (porcentaje) => {
    if (porcentaje >= 70) return 'bg-green-100 border-green-400';
    if (porcentaje >= 40) return 'bg-yellow-100 border-yellow-400';
    if (porcentaje >= 20) return 'bg-orange-100 border-orange-400';
    return 'bg-red-100 border-red-400';
  };

  // Función para obtener el mensaje según el porcentaje
  const getBudgetStatusMessage = (porcentaje) => {
    if (porcentaje >= 70) return 'Presupuesto saludable';
    if (porcentaje >= 40) return 'Presupuesto en seguimiento';
    if (porcentaje >= 20) return 'Presupuesto bajo - Atención requerida';
    return 'Presupuesto crítico - Acción inmediata';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando registros...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">💰 Revisión de Presupuesto META</h1>
            <p className="text-blue-100 mt-2">
              Gestión y seguimiento de presupuestos publicitarios con cálculos automáticos
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-blue-100">Registros Activos</div>
          </div>
        </div>
      </div>

      {/* Reloj en Tiempo Real */}
      <LiveClock />

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setEditingRecord(null);
              setShowForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            Nuevo Registro
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Saludable (70%+)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Seguimiento (40-69%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Bajo (20-39%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Crítico (&lt;20%)</span>
          </div>
        </div>
      </div>

      {/* Lista de registros */}
      <MetaBudgetReviewList
        records={records}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
        getBudgetStatusColor={getBudgetStatusColor}
        getBudgetStatusMessage={getBudgetStatusMessage}
      />

      {/* Modales */}
      {showForm && (
        <MetaBudgetReviewForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {showHistory && selectedRecord && (
        <MetaBudgetReviewHistory
          recordId={selectedRecord.id}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecord(null);
          }}
        />
      )}

      {showQuickAction && quickActionRecord && (
        <MetaBudgetReviewQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default MetaBudgetReviewSection;
